/**
 * The cards' edits, as the decide sends them (`overrides` with `shown`, the choice's args_sha256):
 * the one place that knows the server's shape (`Tool.with_overrides`, `choices.decide`). A card
 * lists the arguments the user may change as `overrides`; without it the card is read-only. A
 * changed press is not run: the server redraws the card at the changes (a new args_sha256) and
 * refuses it with the error code "review_changes", and the next press confirms the card as
 * redrawn.
 */
import { useCallback, useState } from "react";

import type { LastEditedField } from "@/hooks/usePersistentOfferCalc";
import type { OfferValues } from "@/components/underwriting/UnderwritingAssessmentDialogs";
import type { RefinanceCalcValues, RefinanceDriver } from "@/utils/refinanceOfferCalc";

import type { ErrorCode, WireChoice } from "../../protocol/protocol";
import type { CommandError } from "../../socket/client";

/** An edited argument's value as the decide sends it: money as a 2dp string, term an int. */
export type OverrideValue = string | number | string[] | number[];
export type Overrides = Record<string, OverrideValue>;

/** What a press with changes sends beside its option. */
export interface CardEdits {
  overrides: Overrides;
  /** The args_sha256 of the card the changes were made on (CARD_CHANGED when it moved on). */
  shown: string;
}

/** A press that failed for no reason it states. */
export const DECIDE_FAILED = "That didn't go through. Try again.";

/** What the card says once a changed press redrew it: check it and confirm again. */
export const REVIEW_CHANGES = "Your changes are on the card now; check it and confirm again.";

/** The refusal of a changed press whose card the server redrew at the changes (not a failure). */
const REDRAWN: ErrorCode = "review_changes";

/**
 * Whether a decide's rejection is the redraw, by its code: CARD_CHANGED and every other refusal
 * come as "invalid", whatever they say.
 */
export function isRedrawRefusal(reason: unknown): reason is CommandError {
  return (reason as Partial<CommandError> | null)?.code === REDRAWN;
}

const NONE: ReadonlySet<string> = new Set();

/** The arguments a waiting choice's card lets the user change; none: the card is read-only. */
export function editableArgs(choice: WireChoice | null): ReadonlySet<string> {
  if (choice?.status !== "pending" || !choice.args_sha256) return NONE;
  const listed = (choice.card as { overrides?: unknown } | null)?.overrides;
  if (!Array.isArray(listed)) return NONE;
  return new Set(listed.filter((name): name is string => typeof name === "string"));
}

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * What a press sends: each editable argument whose value differs from what the card first
 * showed, without `follows` (terms worked out from the edited ones: the server works them out
 * again). Nothing changed: no edits, a plain decide.
 */
export function changedOverrides(
  editable: ReadonlySet<string>,
  shown: Overrides,
  now: Overrides,
  follows: readonly string[] = []
): Overrides {
  return Object.fromEntries(
    Object.entries(now).filter(
      ([name, value]) =>
        editable.has(name) &&
        !follows.includes(name) &&
        name in shown &&
        !sameJson(value, shown[name])
    )
  );
}

/** The offer args the Offer Builder's values stand for (approve, update_offer_terms). */
export function offerOverrides(values: OfferValues): Overrides {
  return {
    funding_amount: values.fundingAmount.toFixed(2),
    factor: String(values.factor),
    term: values.term,
    frequency: values.frequency,
    merchant_installment: values.installment.toFixed(2),
    added_leverage: values.addedLeverage.toFixed(2),
  };
}

/** The Offer Builder's worked-out terms, by the field it last had typed in. */
const OFFER_DERIVED: [LastEditedField, string][] = [
  ["installment", "merchant_installment"],
  ["addedLeverage", "added_leverage"],
];

/**
 * The offer terms that follow the others: the worked-out ones the user did not type last (typing
 * the funding amount works out the payment and the added leverage; typing the payment, the
 * funding and the leverage).
 */
export function offerFollows(lastEdited: LastEditedField): string[] {
  return OFFER_DERIVED.filter(([field]) => field !== lastEdited).map(([, name]) => name);
}

interface Form {
  sha: string | null;
  /** Each argument's value as the card first showed it. */
  shown: Overrides;
  now: Overrides;
  follows: string[];
}

const EMPTY: Omit<Form, "sha"> = { shown: {}, now: {}, follows: [] };

/**
 * A waiting choice's card edits: the parts of the card `report` what they show (the first report
 * of an argument is what the card showed), and `edits` is what a press sends. A redrawn card (a
 * new args_sha256) starts over.
 */
export function useCardEdits(choice: WireChoice | null) {
  const editable = editableArgs(choice);
  const sha = choice?.args_sha256 ?? null;
  const [form, setForm] = useState<Form>({ sha, ...EMPTY });
  const current = form.sha === sha ? form : { sha, ...EMPTY };

  const report = useCallback(
    (values: Overrides, follows?: string[]) =>
      setForm((prev) => {
        const base = prev.sha === sha ? prev : { sha, ...EMPTY };
        const next = {
          sha,
          shown: { ...values, ...base.shown },
          now: { ...base.now, ...values },
          follows: follows ?? base.follows,
        };
        // A report of what the form already holds keeps it, so the card does not render again.
        return prev.sha === sha && sameJson(next, prev) ? prev : next;
      }),
    [sha]
  );

  /** The press's edits, with `also` (a dialog's values beside what the card showed for them). */
  const edits = (also?: { shown: Overrides; now: Overrides }): CardEdits | undefined => {
    if (!sha || !editable.size) return undefined;
    const overrides = changedOverrides(
      editable,
      { ...current.shown, ...also?.shown },
      { ...current.now, ...also?.now },
      current.follows
    );
    return Object.keys(overrides).length ? { overrides, shown: sha } : undefined;
  };

  return { editable, report, edits };
}

/**
 * The offer args the Offer Builder edits (approve and update_offer_terms list them all): a typed
 * payment is sent as typed, so the terms' rounding cannot move it by a dollar.
 */
const OFFER_ARGS = [
  "funding_amount",
  "factor",
  "term",
  "frequency",
  "merchant_installment",
  "added_leverage",
];

/** What an editable Offer Builder reports to its card's edits. */
export interface OfferEdit {
  onOffer: (values: OfferValues | null) => void;
  onLastEdited: (field: LastEditedField) => void;
  /** Starts the edit over: the card's args_sha256, part of the calculator's key. */
  key: string;
}

/**
 * A card's edits with the Offer Builder on it (approve, update_offer_terms): `offerEdit` when
 * the card lets the user change its terms, and the terms as the user has them (`offer`).
 */
export function useOfferEdits(choice: WireChoice | null) {
  const card = useCardEdits(choice);
  const { report } = card;
  const [offer, setOffer] = useState<OfferValues | null>(null);
  // A null (funding and payment both 0) reports nothing, so it keeps the terms last reported
  // too: the dialog's Our Offer is always what a press would send.
  const onOffer = useCallback(
    (values: OfferValues | null) => {
      if (!values) return;
      setOffer((prev) => (sameJson(prev, values) ? prev : values));
      report(offerOverrides(values));
    },
    [report]
  );
  const onLastEdited = useCallback(
    (field: LastEditedField) => report({}, offerFollows(field)),
    [report]
  );
  const terms = OFFER_ARGS.some((name) => card.editable.has(name));
  const offerEdit: OfferEdit | undefined = terms
    ? { onOffer, onLastEdited, key: choice?.args_sha256 ?? "" }
    : undefined;
  return { ...card, offerEdit, offer: terms ? offer : null };
}

/** The refinance args a refinance card's terms stand for (propose_refinance_offer). */
export function refinanceOverrides(values: RefinanceCalcValues, feePercentage: number): Overrides {
  return {
    refi_amount: values.refiAmount.toFixed(2),
    balance: values.balance.toFixed(2),
    factor: String(values.factor),
    merchant_installment: values.payment.toFixed(2),
    term: values.term,
    frequency: values.frequency,
    origination_fee_percentage: feePercentage.toFixed(2),
  };
}

/** The payment follows the funding amount unless the payment was typed last. */
export function refinanceFollows(lastEdited: RefinanceDriver | null): string[] {
  return lastEdited === "payment" ? [] : ["merchant_installment"];
}
